import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { logger } from '../../utils/logger'
import type { Prompt, PromptCreate, PromptVersion, PromptVersionList, PromptDiff, PromptVisibility } from './types'

const isUuidLike = (val: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val.trim())

/** Platform prompts use stable ids: platform-prompt-<slug> */
export const isPlatformPromptId = (id: string) =>
  typeof id === 'string' && id.startsWith('platform-prompt-')

/** True when value is a UUID or platform-prompt-<slug> (safe for path params). */
export const isCanonicalPromptId = (id: string) =>
  typeof id === 'string' && (isUuidLike(id) || isPlatformPromptId(id))

interface PromptNameRef { id: string; name: string }

/**
 * Map a prompt path ref to a canonical id using an in-memory name→id list.
 * Returns null when the ref is empty, non-canonical, or ambiguous / unmatched.
 */
export function mapPromptRefToId(
  ref: string | null | undefined,
  prompts: PromptNameRef[]
): string | null {
  if (!ref || typeof ref !== 'string') return null
  const trimmed = ref.trim()
  if (!trimmed) return null
  if (isCanonicalPromptId(trimmed)) return trimmed

  const exact = prompts.filter((p) => p.name === trimmed)
  if (exact.length === 1) return exact[0].id
  if (exact.length > 1) return null

  const lower = trimmed.toLowerCase()
  const ci = prompts.filter((p) => p.name.toLowerCase() === lower)
  if (ci.length === 1) return ci[0].id
  return null
}

const assertPromptId = (id: string) => {
  if (!isCanonicalPromptId(id)) {
    throw new Error(`Invalid prompt id: ${id}`)
  }
}

/**
 * For read routes only: if a display name arrives, refresh name→id mapping and
 * retry with the canonical id. Never invent an id — block when unresolved.
 */
async function resolvePromptIdForRead(id: string): Promise<string> {
  const trimmed = typeof id === 'string' ? id.trim() : ''
  if (!trimmed) {
    throw new Error('Missing prompt id')
  }
  if (isCanonicalPromptId(trimmed)) {
    return trimmed
  }

  const { items } = await request<{ items: Prompt[]; total: number }>('/prompts/all', {})
  const resolved = mapPromptRefToId(trimmed, items || [])
  if (!resolved) {
    throw new Error(`Invalid prompt id: ${trimmed}`)
  }
  logger.warn('Resolved prompt display name to canonical id before read', {
    promptName: trimmed,
    promptId: resolved,
  })
  return resolved
}

/** Default page size for GET /prompts/{id}/versions (Agent Studio infinite scroll). */
export const PROMPT_VERSIONS_PAGE_SIZE = 10

const VERSIONS_FETCH_ALL_PAGE_SIZE = 100

export function mergePromptVersions(
  existing: PromptVersion[],
  incoming: PromptVersion[]
): PromptVersion[] {
  const seen = new Set(existing.map((v) => v.commit_id))
  const merged = [...existing]
  for (const version of incoming) {
    if (!seen.has(version.commit_id)) {
      seen.add(version.commit_id)
      merged.push(version)
    }
  }
  return merged
}

/** Map GET /prompts/{id} (optional commit_id) onto a PromptVersion row. */
export function promptDetailToVersion(prompt: Prompt): PromptVersion | null {
  if (!prompt.commit_id) return null
  return {
    commit_id: prompt.commit_id,
    version: prompt.version ?? 0,
    description: prompt.description,
    content: prompt.content || '',
    category: prompt.category,
    commit_message: prompt.commit_message ?? null,
    created_at: prompt.created_at,
    updated_at: prompt.updated_at,
    created_by: prompt.created_by || '',
    updated_by: prompt.updated_by || '',
    creator_name: prompt.creator_name,
    updated_by_name: prompt.updated_by_name,
    is_current: Boolean(prompt.is_current),
    tags: prompt.tags,
  }
}

async function fetchVersionPage(
  canonicalId: string,
  params: { page: number; page_size: number; signal?: AbortSignal }
): Promise<PromptVersionList> {
  const { signal, ...queryParams } = params
  const query = buildQueryString(queryParams)
  return request<PromptVersionList>(`/prompts/${canonicalId}/versions${query}`, {
    signal,
  })
}

export const promptsApi = {
  list: (params?: {
    search?: string
    page?: number
    page_size?: number
    project_id?: string
    category?: string
    creator_id?: string
    model_id?: string
    is_favourite?: boolean
    visibility?: PromptVisibility
    order_by?: 'name' | 'created_at' | 'updated_at'
    order_desc?: boolean
    include_platform?: boolean
    source?: 'platform' | 'user'
    signal?: AbortSignal
    scope?: 'all'
    tenantId?: string
  }) => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Prompt[]; total: number }>(`/prompts${query}`, {
      signal,
      tenantId,
    })
  },

  /** Unpaginated list for dropdowns (e.g. Optimize modal). Uses backend /prompts/all. */
  listAll: (params?: {
    search?: string
    project_id?: string
    category?: string
    creator_id?: string
    model_id?: string
    visibility?: PromptVisibility
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: Prompt[]; total: number }>(`/prompts/all${query}`, {
      signal,
    })
  },

  get: async (id: string, options?: { commit_id?: string; signal?: AbortSignal }) => {
    const canonicalId = await resolvePromptIdForRead(id)
    const { commit_id, signal, ...queryParams } = options || {}
    const query = commit_id ? buildQueryString({ commit_id, ...queryParams }) : ''
    return request<Prompt>(`/prompts/${canonicalId}${query}`, {
      signal,
    })
  },

  create: (data: PromptCreate, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<Prompt>('/prompts', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  /** AI-assisted draft from business context (first version body; refine via editor / optimization). */
  generateFromContext: (
    data: { business_context: string; model_id: string; project_id?: string },
    options?: { signal?: AbortSignal }
  ) =>
    request<{ content: string }>('/prompts/generate-from-context', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  update: (
    id: string,
    data: Partial<PromptCreate> & { source_commit_id?: string | null },
    options?: { signal?: AbortSignal; tenantId?: string }
  ) => {
    assertPromptId(id)
    return request<Prompt>(`/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    })
  },

  delete: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) => {
    assertPromptId(id)
    return request<void>(`/prompts/${id}`, {
      method: 'DELETE',
      signal: options?.signal,
      tenantId: options?.tenantId,
    })
  },

  bulkDelete: (promptIds: string[], options?: { force?: boolean; signal?: AbortSignal; tenantId?: string }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/prompts/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ prompt_ids: promptIds }),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  getVersions: async (
    id: string,
    options?: { page?: number; page_size?: number; signal?: AbortSignal }
  ) => {
    const canonicalId = await resolvePromptIdForRead(id)
    return fetchVersionPage(canonicalId, {
      page: options?.page ?? 1,
      page_size: options?.page_size ?? PROMPT_VERSIONS_PAGE_SIZE,
      signal: options?.signal,
    })
  },

  /** Walk paginated version history until all rows are loaded. */
  getAllVersions: async (id: string, options?: { signal?: AbortSignal }) => {
    const canonicalId = await resolvePromptIdForRead(id)
    const first = await fetchVersionPage(canonicalId, {
      page: 1,
      page_size: VERSIONS_FETCH_ALL_PAGE_SIZE,
      signal: options?.signal,
    })
    const items = [...(first.items || [])]
    const total = first.total ?? items.length
    const totalPages = Math.max(1, Math.ceil(total / VERSIONS_FETCH_ALL_PAGE_SIZE))
    for (let page = 2; page <= totalPages; page++) {
      const next = await fetchVersionPage(canonicalId, {
        page,
        page_size: VERSIONS_FETCH_ALL_PAGE_SIZE,
        signal: options?.signal,
      })
      items.push(...(next.items || []))
    }
    return items
  },

  fork: (id: string, commit_id: string, options?: { signal?: AbortSignal }) => {
    assertPromptId(id)
    return request<Prompt>(`/prompts/${id}/fork?commit_id=${commit_id}`, {
      method: 'POST',
      signal: options?.signal,
    })
  },

  getDiff: async (
    id: string,
    baseCommitId: string,
    compareCommitId: string,
    options?: { signal?: AbortSignal }
  ) => {
    const canonicalId = await resolvePromptIdForRead(id)
    const query = buildQueryString({
      base_commit_id: baseCommitId,
      compare_commit_id: compareCommitId,
    })
    return request<PromptDiff>(`/prompts/${canonicalId}/diff${query}`, {
      signal: options?.signal,
    })
  },

  /** Clone a prompt for a different model. Creates a new prompt with all versions copied. */
  cloneForModel: (id: string, modelId: string, options?: { signal?: AbortSignal; tenantId?: string }) => {
    assertPromptId(id)
    const query = buildQueryString({ model_id: modelId })
    return request<Prompt>(`/prompts/${id}/clone-for-model${query}`, {
      tenantId: options?.tenantId,
      method: 'POST',
      signal: options?.signal,
    })
  },
}
